import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

import { FormsModule, NgForm } from '@angular/forms';
import { RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ISizeName } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ActionComponent } from '../../ui/api/action.component';
import { AddressFieldsetComponent } from '../../ui/form/address-fieldset/address-fieldset.component';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../ui/modal/modal-layout-right.component';
import { ResultComponent } from '../../ui/resut/result.component';

export interface IModalData {
    addressData: Checkout.IAddress;
    onSaveAddress: () => void;
}

export namespace NCheckoutAddressModal {
    export const componentId = 'checkout-address-modal';
    export interface IBlockContent {
        title: string;
        saveBtnCopy?: string;
        closeBtnCopy?: string;
    }
}

@DfCmsModal({
    id: NCheckoutAddressModal.componentId,
    name: 'Checkout Address Modal',
    position: (sizeId: ISizeName) => (sizeId === 'S' ? 'bottom' : 'right'),
    config: sizeId => ({
        ...NModalLayoutRight.ModalConfig,
        maxWidth: sizeId === 'S' ? '100vw' : '30.5rem',
        height: sizeId === 'S' ? '60vh' : '100vh',
        position:
            sizeId === 'S'
                ? { bottom: '0' }
                : {
                      top: '0',
                      right: '0',
                      bottom: '0'
                  },
        hasBackdrop: true
    }),
    prerenderAllowed: false,
    reopenPolicy: NDfModal.ReopenPolicy.last,
    sizeRestriction: sizeId => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X'])
})
@ICmsComponent<CheckoutAddressModalComponent>({
    name: 'Checkout Address Modal',
    id: NCheckoutAddressModal.componentId,
    block: {
        url: NCheckoutAddressModal.componentId
    },
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'Edit address',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'saveBtnCopy',
            title: 'Save button copy',
            default: 'Save',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'closeBtnCopy',
            title: 'Close button copy',
            default: 'Cancel',
            required: false
        }
    ]
})
@Component({
    selector: 'checkout-address-modal',
    templateUrl: './checkout-address-modal.component.html',
    standalone: true,
    imports: [ModalLayoutRightComponent, ICmsClientModule, AddressFieldsetComponent, ActionComponent, ResultComponent, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutAddressModalComponent extends UiComponent implements OnInit, NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCheckoutAddressModal.IBlockContent;

    private modalRef = inject(ModalRef<any>);

    protected data = inject(MODAL_DATA) as IModalData;

    private requestService = inject(RequestService);

    @ViewChild('editForm', { static: false })
    protected form?: NgForm;

    protected readonly status = new Status();

    protected close() {
        this.modalRef.close();
    }

    protected async saveAddress() {
        this.status.busy = true;
        try {
            const promise = this.requestService.submitForm(this.form, undefined, () =>
                this.requestService.nexus.customer.saveAddress(this.data.addressData)
            );
            if (!promise) {
                this.status.busy = false;
                return;
            }
            await promise;
            this.data.onSaveAddress();
            this.close();
        } catch (error) {
            this.status.error = error as any;
        }
    }
}
